import {
  plus,
  minus,
  multipliedBy,
  dividedBy,
  decimalPlaces,
  toCent,
  toYuan,
  formatChineseMoney,
  toSecond,
  toMillisecond,
} from '@/utils/number';

const BEIJING_TIME_OFFSET_SECONDS = 8 * 60 * 60;

function padZero(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

function getBeijingDate(timeStamp) {
  // 北京时间固定为东八区（UTC+8），不随管理员电脑/手机时区变化。
  // 将时间戳加上 8 小时后读取 UTC 字段，即可得到北京时间。
  const timestamp = +timeStamp;
  return new Date((timestamp + BEIJING_TIME_OFFSET_SECONDS) * 1000);
}

function getBeijingDateParts(timeStamp) {
  const date = getBeijingDate(timeStamp);

  return {
    Y: date.getUTCFullYear(),
    m: padZero(date.getUTCMonth() + 1),
    d: padZero(date.getUTCDate()),
    H: padZero(date.getUTCHours()),
    i: padZero(date.getUTCMinutes()),
    s: padZero(date.getUTCSeconds()),
  };
}

function getDate({
  timeStamp,
  format = 'Y-m-d H:i:s',
  emptyString,
  timeAgo = true,
}) {
  if (!timeStamp) return emptyString;

  const date = new Date(timeStamp * 1000);
  const diff = ((new Date()).getTime() - date.getTime()) / 1000;

  if (timeAgo) {
    if (diff >= 3600 && diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
    if (diff >= 60 && diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
    if (diff < 60) return '刚刚';
  }

  const { Y, m, d, H, i, s } = getBeijingDateParts(timeStamp);

  let result = '';
  if (format.includes('Y')) result += Y;
  if (format.includes('m')) result += `-${m}`;
  if (format.includes('d')) result += `-${d}`;
  if (format.includes('H')) result += ` ${H}:`;
  if (format.includes('i')) result += `${i}`;
  if (format.includes('s')) result += `:${s}`;
  return result;
}

const timeAgoWithYmd = (timeStamp, emptyString = '-') => getDate({ timeStamp, format: 'Y-m-d', emptyString });
const timeAgoWithYmdHi = (timeStamp, emptyString = '-') => getDate({ timeStamp, format: 'Y-m-d H:i', emptyString });
const timeAgoWithYmdHis = (timeStamp, emptyString = '-') => getDate({ timeStamp, format: 'Y-m-d H:i:s', emptyString });

const timeToYm = (timeStamp, emptyString = '-') => getDate({
  timeStamp,
  format: 'Y-m',
  emptyString,
  timeAgo: false,
});
const timeToYmd = (timeStamp, emptyString = '-') => getDate({
  timeStamp,
  format: 'Y-m-d',
  emptyString,
  timeAgo: false,
});
const timeToYmdHi = (timeStamp, emptyString = '-') => getDate({
  timeStamp,
  format: 'Y-m-d H:i',
  emptyString,
  timeAgo: false,
});
const timeToYmdHis = (timeStamp, emptyString = '-') => getDate({
  timeStamp,
  format: 'Y-m-d H:is',
  emptyString,
  timeAgo: false,
});
// 中文日期格式：xxxx年xx月xx日 时:分
const timeToCn = (timeStamp, emptyString = '-') => {
  if (!timeStamp) return emptyString;
  const { Y, m, d, H, i } = getBeijingDateParts(timeStamp);
  return `${Y}年${m}月${d}日 ${H}:${i}`;
};

// 课程日期（北京时间 Y-m-d）。日期一律带年份：筛选里有「全部」和跨年区间，只给月日没法判断是哪一年。
// 下课在次日（跨零点）时把「次日」补在日期这行——时段行必须保持定长 HH:mm - HH:mm，
// 多两个字就会超出列宽自己折行（变成三行）
const formatCourseDate = (startTime, endTime) => {
  if (!startTime) return '-';

  const { Y, m, d } = getBeijingDateParts(startTime);
  const date = `${Y}-${m}-${d}`;
  if (!endTime) return date;

  const end = getBeijingDateParts(endTime);
  return end.Y === Y && end.m === m && end.d === d ? date : `${date} 次日`;
};

const WEEKDAY_NAMES = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

// 课程周几（按北京时间取星期，周日为 0）
const formatCourseWeekday = (timeStamp) => {
  if (!timeStamp) return '';
  return WEEKDAY_NAMES[getBeijingDate(timeStamp).getUTCDay()];
};

// 课程时段（周一 H:i - H:i，周几和时段都定长，正常不会折行）
const formatCourseTimeRange = (startTime, endTime) => {
  if (!startTime || !endTime) return '-';

  const start = getBeijingDateParts(startTime);
  const end = getBeijingDateParts(endTime);
  return `${formatCourseWeekday(startTime)} ${start.H}:${start.i} - ${end.H}:${end.i}`;
};

const timeToHi = (timeStamp, emptyString = '-') => getDate({
  timeStamp,
  format: 'H-i',
  emptyString,
  timeAgo: false,
});

const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return '未设置';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const parts = [];
  if (days > 0) parts.push(`${days} 天`);
  if (hours > 0) parts.push(`${hours} 小时`);
  if (minutes > 0) parts.push(`${minutes} 分钟`);
  return parts.length ? parts.join(' ') : '0 分钟';
};

// 门店库中 store_name 不带“店”字，展示时统一拼接；多个店名以、/, 分隔时逐个拼接。
const formatStoreName = (value) => {
  const name = String(value == null ? '' : value).trim();
  if (!name) return '';
  const items = name.split(/[、,，]/).map((item) => item.trim()).filter(Boolean);
  return items.map((item) => (/店$/.test(item) ? item : `${item}店`)).join('、');
};

// 金额展示：千分位 + 固定两位小数，避免同一页面出现「12345」「647.75」两种写法
const MONEY_TEXT_FORMAT = new Intl.NumberFormat('zh-CN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// 分 → 「12,345.00」
const formatCent = (cents) => {
  const value = Number(cents);
  return MONEY_TEXT_FORMAT.format(Number.isFinite(value) ? value / 100 : 0);
};

// 元 → 「12,345.00」
const formatYuan = (yuan) => {
  const value = Number(yuan);
  return MONEY_TEXT_FORMAT.format(Number.isFinite(value) ? value : 0);
};

const defaultAvatar = 'https://img.jiahewushe.com/icon/avatar.png';

const httpsAvatar = (link) => (link ? link.replace('http:', '') : defaultAvatar);

export {
  timeToYm,
  timeToYmd,
  timeToYmdHi,
  timeToYmdHis,
  timeToHi,
  timeToCn,
  formatCourseDate,
  formatCourseTimeRange,
  timeAgoWithYmd,
  timeAgoWithYmdHi,
  timeAgoWithYmdHis,
  httpsAvatar,
  plus,
  minus,
  multipliedBy,
  dividedBy,
  decimalPlaces,
  toCent,
  toYuan,
  formatCent,
  formatYuan,
  formatChineseMoney,
  toSecond,
  toMillisecond,
  formatDuration,
  formatStoreName,
  defaultAvatar,
};
